// The ADD-ALARM PICKER's pure half (`.context/alarms-redesign/spec-all.md`,
// "Add-alarm picker — PHASE 3"): everything the panel decides before it renders
// anything. No React, no DOM — `cloud/web`'s runner has no DOM, so a rule that
// needs a mount is a rule that never gets a test.
//
// The picker turns the boat's SIGNAL CATALOG — the accepted, compiler-bindable
// paths served by `GET /v1/boats/:boatId/signals` — into "what should the boat
// watch?": a category rail, one row per signal, and a pre-filled custom-alarm
// draft for the row that gets picked. It invents nothing:
//
//   - The categories are the `signalCatalog.signalGroup` buckets ACTUALLY
//     present on this boat, in the union's own declared order. A boat with no
//     steering gear has no Steering category; a group is never listed with a
//     zero count, because a category that opens on an empty list reads as a
//     broken panel rather than as an honest "you have none of these".
//   - The live value and its freshness come from the same
//     `signalLive.signalReading` rule the rest of the app reads a catalog signal
//     with (live frame vs stored snapshot, ranked BY TIME), so the picker cannot
//     print a different "now" from the row list behind it.
//   - The draft's defaults come from the SIGNAL's kind
//     (`suggestedTriggerClasses`) and from `customAlarmForm.initCustomDraft`.
//     The SET POINT is deliberately left BLANK: nothing in the catalog says what
//     a safe voltage or a hot engine is for this boat, and a prefilled threshold
//     is a number the owner did not choose being armed on their behalf. The
//     empty field is what {@link createBlocker} then reports, which is exactly
//     the design's "CREATE is disabled until the sentence is complete".
//
// The one number this module does NOT compute is a mini-bar: the catalog carries
// no min/max scale for an arbitrary signal (data-mapping trap, NOW cell), so the
// design's 110×10 bar has no honest domain here and is dropped rather than drawn
// against an invented 0..100.

import type { UnitSettings } from '@neomaris/protocol';

import { readCustomSpec, resolvedPathFor } from './alarmDisplay';
import { defaultTitle } from './alarmSentence';
import { categoryFor } from './displayUnits';
import {
  customValueUnits,
  draftThresholds,
  errorFor,
  initCustomDraft,
  setSignal,
  setTriggerClass,
  syncAutoTitle,
  validate,
  type CustomAlarmDraft,
} from './customAlarmForm';
import {
  freshness,
  signalDisplayName,
  signalGroup,
  suggestedTriggerClasses,
  type Signal,
  type SignalFreshness,
  type SignalGroup,
} from './signalCatalog';
import { formatReading, signalReading, type LiveSampleLike } from './signalLive';

/**
 * The category rail's order — the `SignalGroup` union's own declared order,
 * which runs equipment-first (Engine, Batteries, Switches, Electrical, Tanks)
 * then the ambient and derived families. Fixed, so the rail does not reshuffle
 * itself when a signal appears or goes quiet.
 */
export const SIGNAL_GROUP_ORDER: readonly SignalGroup[] = [
  'Engine',
  'Batteries',
  'Switches',
  'Electrical',
  'Tanks',
  'Environment',
  'Navigation',
  'Steering',
  'Derived',
  'Other',
];

/** One entry in the category rail. */
export interface PickerCategory {
  readonly group: SignalGroup;
  /** How many catalog signals file under it. Never zero — empties are dropped. */
  readonly count: number;
}

/**
 * The categories this boat actually has, in {@link SIGNAL_GROUP_ORDER}, with
 * their counts. Empty groups are dropped: the rail describes THIS boat.
 */
export function pickerCategories(signals: readonly Signal[]): PickerCategory[] {
  const counts = new Map<SignalGroup, number>();
  for (const signal of signals) {
    const group = signalGroup(signal.path);
    counts.set(group, (counts.get(group) ?? 0) + 1);
  }
  const out: PickerCategory[] = [];
  for (const group of SIGNAL_GROUP_ORDER) {
    const count = counts.get(group);
    if (count !== undefined && count > 0) out.push({ group, count });
  }
  return out;
}

/**
 * The signals in one category, ordered the way they are READ — by display name,
 * with the raw path breaking a tie so two identically-labelled channels keep a
 * stable order between renders.
 */
export function signalsInGroup(signals: readonly Signal[], group: SignalGroup): Signal[] {
  return signals
    .filter((s) => signalGroup(s.path) === group)
    .sort((a, b) => {
      const byName = signalDisplayName(a).localeCompare(signalDisplayName(b));
      return byName !== 0 ? byName : a.path.localeCompare(b.path);
    });
}

/**
 * The category to open on: the first one that has signals, or `null` for a boat
 * whose catalog is empty. Never a hard-coded "Power" — a boat with no batteries
 * would open the panel on an empty list.
 */
export function firstCategory(categories: readonly PickerCategory[]): SignalGroup | null {
  return categories[0]?.group ?? null;
}

// ---------------------------------------------------------------------------
// "N alarms exist" — how many definitions already watch this path
// ---------------------------------------------------------------------------

/** The definition fields the count reads. A structural subset of `AlarmDefView`. */
export interface AlarmPathDef {
  readonly compileStatus?: string | null;
  readonly resolvedPath?: string | null;
  readonly custom?: unknown;
  readonly binding?: unknown;
}

/**
 * The path a definition watches, for counting purposes only: the compiler's own
 * resolution, else the path a CUSTOM alarm names itself.
 *
 * The fallback is the point. `resolvedPathFor` answers `null` while a definition
 * is compile-pending (ADR 0170 — a pending def has no bound signal, and guessing
 * one is forbidden), so counting on it alone would drop the alarm the owner just
 * created from this very panel: the receipt says created, and the row underneath
 * still says nothing watches this signal. A custom alarm's own `custom.path` is
 * not a guess — it is what the owner pinned — so it counts.
 */
export function watchedPathFor(def: AlarmPathDef): string | null {
  const resolved = resolvedPathFor(def);
  if (resolved !== null) return resolved;
  const custom = readCustomSpec(def)?.path;
  return custom === undefined || custom.length === 0 ? null : custom;
}

/** How many definitions already watch each path. */
export function alarmCountsByPath(defs: Iterable<AlarmPathDef>): Map<string, number> {
  const counts = new Map<string, number>();
  for (const def of defs) {
    const path = watchedPathFor(def);
    if (path === null) continue;
    counts.set(path, (counts.get(path) ?? 0) + 1);
  }
  return counts;
}

/**
 * The row's note — "1 alarm exists" / "3 alarms exist" — or `null` when nothing
 * watches this signal yet. Silence is the honest cell for zero: "0 alarms exist"
 * is noise on every row of a fresh boat.
 */
export function existingAlarmsNote(count: number): string | null {
  if (count <= 0) return null;
  return count === 1 ? '1 alarm exists' : `${count} alarms exist`;
}

// ---------------------------------------------------------------------------
// Freshness — the row treatment
// ---------------------------------------------------------------------------

/**
 * An age in the largest unit that still reads at a glance: `4 s`, `7 min`,
 * `3 hr`, `24 d`. The picker's freshness cell is 100px wide and sits beside a
 * value, so "24 days ago" is both too long and the wrong emphasis — the design
 * words it as an AGE ("24 d old"), not as a moment.
 */
export function shortAge(ms: number): string {
  const safe = Math.max(0, ms);
  const sec = Math.floor(safe / 1000);
  if (sec < 60) return `${sec} s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr`;
  return `${Math.floor(hr / 24)} d`;
}

/** A row's freshness cell, and whether the row wears the gone-quiet treatment. */
export interface RowFreshness {
  /** The band, from `signalCatalog.freshness` — one source of truth for all screens. */
  readonly band: SignalFreshness;
  /** "live · 4 s", "24 d old ⚠", or "no reading" for a path that never reported. */
  readonly text: string;
  /**
   * True outside the fresh band: the design greys the name and the value and
   * marks the age, because a number the boat last sent days ago must not read
   * as something you can set a threshold against right now.
   */
  readonly dim: boolean;
}

/**
 * Bucket one row's reading time. `null` — an accepted path that has never
 * carried a value — is `cold` and says so in words, never "just now".
 */
export function rowFreshness(atMs: number | null, now: number = Date.now()): RowFreshness {
  if (atMs === null || !Number.isFinite(atMs)) {
    return { band: 'cold', text: 'no reading', dim: true };
  }
  const iso = new Date(atMs).toISOString();
  const band = freshness(iso, now);
  const age = shortAge(now - atMs);
  return band === 'fresh'
    ? { band, text: `live · ${age}`, dim: false }
    : { band, text: `${age} old ⚠`, dim: true };
}

// ---------------------------------------------------------------------------
// One rendered row
// ---------------------------------------------------------------------------

/** Everything a picker row prints, decided once and testable. */
export interface PickerRow {
  readonly path: string;
  /** `signalDisplayName` — the installer's channel label when there is one. */
  readonly name: string;
  /** The device it comes from, when the catalog names one. */
  readonly sub: string | null;
  /** The reading with its unit ("12.7 V", "On"), or `null` when there is none. */
  readonly valueText: string | null;
  readonly freshness: RowFreshness;
  /** "2 alarms exist", or `null`. */
  readonly note: string | null;
}

/**
 * One row, resolved. The reading goes through `signalLive.signalReading` /
 * `formatReading` — the same rule the custom builder's live hint uses, which
 * ranks the live frame against the stored snapshot BY TIME and prints a switch
 * channel's 1/0 as On/Off rather than as a number nobody can read.
 *
 * `options.units` restates the reading in the account's units (ADR 0189). The
 * category is resolved from the signal's own PATH, exactly as every other live
 * row on the alarm surfaces resolves it, so a row here and the same signal's row
 * on the Alarms page cannot print two different numbers. Omit it and every row
 * reads in the boat's unit, as before.
 */
export function pickerRow(
  signal: Signal,
  sample: LiveSampleLike | null | undefined,
  options: {
    readonly alarmCount?: number;
    readonly now?: number;
    readonly units?: UnitSettings;
  } = {}
): PickerRow {
  const reading = signalReading(signal, sample);
  const device = signal.deviceName?.trim();
  return {
    path: signal.path,
    name: signalDisplayName(signal),
    sub: device === undefined || device.length === 0 ? null : device,
    valueText: formatReading(reading, categoryFor(signal.path, reading.unit), options.units),
    freshness: rowFreshness(reading.atMs, options.now ?? Date.now()),
    note: existingAlarmsNote(options.alarmCount ?? 0),
  };
}

// ---------------------------------------------------------------------------
// The draft the picked signal starts from
// ---------------------------------------------------------------------------

/**
 * A fresh CUSTOM draft pinned to one signal, with the trigger class the signal's
 * KIND suggests first — `High` for a number, `On` for a switch or indicator
 * channel (`suggestedTriggerClasses` knows that a bank channel travelling as a
 * 1/0 number is a flag, not a measurement), `DataUnavailable` for anything a v1
 * alarm can only watch for silence.
 *
 * Every other default is `initCustomDraft`'s: warning severity, watch always,
 * no delays, notify remotely. The SET POINT stays empty on purpose — see the
 * module header.
 *
 * `units` is the account's, and it rides the draft so the empty set-point box
 * this returns is read and written in the owner's unit (ADR 0189). `setSignal`
 * brings the signal's DECLARED store unit along, which is the other half the
 * conversion needs. Omit it and the box is the boat's unit, as before.
 */
export function draftForSignal(signal: Signal, units?: UnitSettings): CustomAlarmDraft {
  const pinned = setSignal(initCustomDraft(units), signal);
  const suggested = suggestedTriggerClasses(signal.kind, signal.path)[0];
  return suggested === undefined ? pinned : setTriggerClass(pinned, suggested);
}

/**
 * The name this draft suggests for itself — the review sentence, cut to the
 * API's 200-character bound (`alarmSentence.defaultTitle`). `null` before there
 * is a trigger class to describe.
 *
 * The picker has no name field, so an unnamed draft would fail validation with
 * nothing on screen to fix; the suggestion is what keeps CREATE reachable, and
 * it tracks the sentence as the set point is typed.
 */
export function autoTitleFor(draft: CustomAlarmDraft, signal: Signal): string | null {
  const triggerClass = draft.triggerClass;
  if (triggerClass === undefined) return null;
  // The same words the review sentence uses, in the same unit — the name is
  // PREFILLED FROM that sentence, and a title left in store units under a
  // sentence in the account's would read as two different alarms (and would
  // stop the list row de-duplicating the two).  `draftThresholds` is
  // store-domain, so `category` + `units` are what restate it. The numbers are
  // the stored ones, not the sentence's rounded ones: the title is stored, and
  // the Alarms page rounds it when it shows it (`displayedTitle`, TOM-860).
  const boxUnits = customValueUnits(draft);
  return defaultTitle({ triggerClass, path: signal.path }, draftThresholds(draft), {
    label: signalDisplayName(signal),
    unit: signal.unit,
    ...(boxUnits.category === undefined ? {} : { category: boxUnits.category }),
    ...(boxUnits.settings === undefined ? {} : { units: boxUnits.settings }),
  });
}

/** {@link autoTitleFor}, applied — an untouched title follows the sentence. */
export function withAutoTitle(draft: CustomAlarmDraft, signal: Signal): CustomAlarmDraft {
  const suggestion = autoTitleFor(draft, signal);
  if (suggestion === null || suggestion.length === 0) return draft;
  return syncAutoTitle(draft, suggestion);
}

/**
 * What stops this draft being created, in the operator's words, or `null` when
 * it is ready. The builder's own {@link validate} decides — one validation, so
 * the picker cannot accept a draft the full builder would refuse (or refuse one
 * it would accept).
 *
 * The TITLE is deliberately not reported: the picker fills it from the sentence
 * and shows no name field, so "Give this alarm a name" would name a control that
 * is not on screen. It cannot fire in practice — {@link withAutoTitle} runs on
 * every draft change — and if it ever did, the generic fallback is the honest
 * thing to say.
 */
export function createBlocker(draft: CustomAlarmDraft, catalog: readonly Signal[]): string | null {
  const errors = validate(draft, catalog);
  if (errors.length === 0) return null;
  const visible = errors.find((e) => e.field !== 'title');
  if (visible !== undefined) return visible.message;
  return errorFor(errors, 'title') === undefined
    ? null
    : 'This alarm needs a name — open the full builder to give it one.';
}
